<template>
  <div class="wish-frame">
    <div class="real-wish" @contextmenu="tryShowActions">
      <h3 class="accessibility oob" tabindex="0">{{ headline }}</h3>
      <surface-post-author v-if="xAuthor"
                           :post="post"
                           :user="user"
                           class="author" />
      <section v-if="xWords" class="words">
        <surface-post-words :post="post"
                            :is-editable-by-current-user="isEditableByCurrentUser"
                            @edit="startEditingPost({postCid: post.cid})" />
      </section>
      <surface-post-attachment v-if="xAttachment" :post="post" :is-being-edited="false" />
      <surface-post-moderation v-if="xModeration"
                               :post="post"
                               :can-i-moderate="canIModerate"
                               :is-being-edited="false"
                               @approve="approvePost({postCid: post.cid})"
                               @delete="confirmDelete" />
      <section v-if="xComments || xReactions" class="stalker-bar">
        <surface-post-reaction v-if="xReactions"
                               :post="post"
                               :is-reacting="isBeingReacted"
                               :is-reactable="isReactable"
                               :user-id="user.id"
                               :reaction-data="reactionData"
                               :reaction-ids="postReactionIds"
                               :reactions="wallReactions" />
        <surface-post-comments v-if="xComments && !xPostCommentsInPanel"
                               :comment-being-edited-id="commentBeingEditedId"
                               :post="post"
                               :user="user"
                               :comment-ids="postCommentIds"
                               :comments="wallComments"
                               :is-commentable="isCommentable"
                               @create="createComment($event)"
                               @show-comment-action-menu="showCommentActionMenu($event)"
                               @stop-editing="stopEditingComment"
                               @edit="editComment($event)" />
        <surface-post-comments-icon v-if="xComments && xPostCommentsInPanel"
                                    :comment-ids="postCommentIds"
                                    @show-comments-panel="showCommentsPanel({postId: post.id})" />
      </section>
    </div>
    <surface-post-actions :x-author="xAuthor"
                          :post="post"
                          :luminance="luminance"
                          :is-editable-by-current-user="isEditableByCurrentUser"
                          :is-being-edited="false"
                          @delete="confirmDelete"
                          @edit="startEditingPost({postCid: post.cid})"
                          @dropdown="showPostActionMenu({postCid: post.cid})" />
  </div>

</template>

<script>

import { mapState, mapActions, mapGetters } from 'vuex'
import SurfacePostAuthor from 'vuecomponents/surface_post_author'
import SurfacePostWords from 'vuecomponents/surface_post_words'
import SurfacePostActions from 'vuecomponents/surface_post_actions'
import SurfacePostAttachment from 'vuecomponents/surface_post_attachment'
import SurfacePostModeration from 'vuecomponents/surface_post_moderation'
import SurfacePostReaction from 'vuecomponents/surface_post_reaction'
import SurfacePostComments from 'vuecomponents/surface_post_comments'
import SurfacePostCommentsIcon from 'vuecomponents/surface_post_comments_icon'
import IntlMixin from 'vuemixins/intl_mixin'
import { isUrl } from 'bits/url'
import { $ } from 'bits/jquery'

export default {
  components: {
    SurfacePostAuthor,
    SurfacePostWords,
    SurfacePostAttachment,
    SurfacePostActions,
    SurfacePostModeration,
    SurfacePostReaction,
    SurfacePostComments,
    SurfacePostCommentsIcon,
  },
  mixins: [IntlMixin],
  props: ['post', 'index', 'edit', 'isLast'],
  computed: {
    ...mapState({
      user: 'user',
      isMobile: 'isMobile',
    }),
    ...mapState('reaction', {
      wallReactions: 'reactionEntities',
      postIdBeingReactedTo: 'postIdBeingReactedTo',
    }),
    ...mapState('comment', {
      commentBeingEditedId: 'commentBeingEditedId',
      wallComments: 'commentEntities',
    }),
    ...mapState('postConnection', ['postToConnectFromId']),
    ...mapGetters([
      'xAuthor',
      'reactionData',
      'isCommentable',
      'isReactable',
      'canIModerate',
      'canIWrite',
      'xPostCommentsInPanel',
    ]),
    ...mapGetters('reaction', {
      wallReactionIdsByPost: 'reactionIdsByPost',
    }),
    ...mapGetters('comment', {
      wallCommentIdsByPost: 'commentIdsByPost',
    }),
    isPublished ()         { return this.post.published },
    subject ()             { return this.post.subject },
    body ()                { return this.post.body },
    headline ()            { return this.post.headline },
    isBeingReacted ()      { return this.postIdBeingReactedTo === this.post.id },
    hasAttachment () {
      const attachment = this.post.attachment
      return !!attachment && isUrl(attachment)
    },
    xWords ()      { return this.subject || this.body },
    xAttachment () { return this.hasAttachment },
    xComments ()   {
      return this.isPublished &&
            (this.isCommentable || this.postCommentIds.length > 0)
    },
    xModeration () { return !this.isPublished },
    xReactions ()  { return (this.isReactable || this.postReactionIds.length > 0) },
    xConnect ()    {
      return (
        !!this.postToConnectFromId &&
        this.postToConnectFromId !== this.post.id &&
        !this.areConnected(this.post.id, this.postToConnectFromId)
      )
    },
    postCommentIds ()    { return this.wallCommentIdsByPost[this.post.id] || [] },
    postReactionIds () {
      return this.wallReactionIdsByPost[this.post.id] || []
    },
    luminance ()              { return $('.beethoven-preview-image', this.$el).data('luminance') },
    wasWrittenByCurrentUser () { return !!this.user.id && this.post.author_id === this.user.id },
    // Either the current user is a moderator or current user is the author and wall is writable
    isEditableByCurrentUser () {
      return this.canIModerate ||
            (this.wasWrittenByCurrentUser && this.canIWrite)
    },
  },
  methods: {
    ...mapActions(['showCommentsPanel']),
    ...mapActions('post', [
      'startEditingPost',
      'approvePost',
      'deletePost',
    ]),
    ...mapActions('postAction', [
      'showPostActionMenu',
    ]),
    ...mapActions('comment', [
      'createComment',
      'editComment',
      'stopEditingComment',
      'startEditingComment',
      'showCommentActionMenu',
    ]),
    areConnected (post1Id, post2Id) {
      return this.connections.filter((c) => {
        return (c.from_wish_id === post1Id && c.to_wish_id === post2Id) ||
          (c.from_wish_id === post2Id && c.to_wish_id === post1Id)
      }).length > 0
    },
    confirmDelete () {
      if (!this.post.id /* post is new */ || (!this.post.body && !this.post.subject && !this.post.attachment) || confirm(this.__('Are you sure you want to delete this post? This cannot be undone!'))) {
        this.deletePost({ postCid: this.post.cid })
      }
    },
    tryShowActions (e) {
      if (this.isMobile || this.xConnect || $(e.target).is('a,textarea,input,select,option') || e.shiftKey) {
        return
      }
      e.preventDefault()
      this.showPostActionMenu({ postCid: this.post.cid })
    },
  },
}
</script>

<style lang="scss">
.stalker-bar {
  html[data-layout=map] & {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    width: 100%;
    background-color: rgba(black, 0.04);
    height: 34px;
    min-height: 34px; //safari
    .surface-post-reaction {
      background-color: transparent !important;
    }
    .surface-post-comments {
      background-color: transparent !important;
    }
  }
  html > body[data-color-scheme=dark] & {
    background-color: rgba(black, 0.06);
  }
}
</style>
